import React from 'react';
import { Loader2 } from 'lucide-react';
import { Badge, type BadgeProps } from '../../components/ui/badge';
import {
  type AdminAccountPaymentSummaryAppGroup,
  type AdminAccountPaymentSummaryResponse,
  formatAdminAccountDateTime,
  formatAdminAccountPaymentPlatform,
} from '../../types/account';

export function AccountSummaryCell({
  summary,
  loading,
  hasError,
}: {
  summary?: AdminAccountPaymentSummaryResponse;
  loading: boolean;
  hasError: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        이용·결제 요약을 불러오는 중입니다...
      </div>
    );
  }

  if (hasError && (!summary || summary.appGroups.length === 0)) {
    return <span className="text-sm text-amber-700">요약 로드 실패</span>;
  }

  if (!summary || summary.appGroups.length === 0) {
    return <span className="text-sm text-muted-foreground">이용·쿠폰 이력 없음</span>;
  }

  return (
    <div className="space-y-1.5">
      {summary.appGroups.map((group) => {
        const primarySummary = getPrimarySummary(group);
        const access = getAccessDisplay(group);
        const accessExpiry = getAccessExpiryDisplay(group);
        const nextPayment = getNextPaymentDisplay(group);
        const historyBadges = getHistoryBadges(group);
        const hasPaymentFailure = group.nextPaymentState === 'PAYMENT_FAILURE'
          || group.summaries.some((item) => {
            const category = item.failureCategory?.trim().toLowerCase();
            return item.status.trim().toUpperCase() === 'PAYMENT_FAILURE'
              || category === 'renewal_payment_failure'
              || category === 'billing_payment_failure';
          });

        return (
          <div
            key={group.appKey}
            className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-2 text-xs leading-relaxed"
          >
            <Badge variant={group.appKey.toUpperCase() === 'SAJU' ? 'default' : 'secondary'}>
              {group.appLabel}
            </Badge>
            <span className="font-medium text-foreground">
              {primarySummary?.planLabel || primarySummary?.productPlanCode || '플랜 없음'}
            </span>
            <span className="text-muted-foreground">·</span>
            <Badge variant={access.variant} title={access.detail}>{access.label}</Badge>
            {group.currentAccessReference ? (
              <Badge variant="outline" title={`현재 이용 출처 식별자 · ${formatAdminAccountDateTime(group.currentAccessStartedAt)}`}>
                {formatCurrentAccessReference(group)}
              </Badge>
            ) : null}
            {historyBadges.map((badge) => (
              <Badge key={badge.label} variant={badge.variant} title={badge.detail}>{badge.label}</Badge>
            ))}
            <span className="text-muted-foreground">·</span>
            <Badge variant={nextPayment.variant} title={nextPayment.detail}>{nextPayment.label}</Badge>
            {hasPaymentFailure && group.nextPaymentState !== 'PAYMENT_FAILURE' ? (
              <Badge variant="destructive">결제 실패 이력</Badge>
            ) : null}
            {group.summaries.length > 1 ? (
              <Badge variant="outline">+{group.summaries.length - 1}개</Badge>
            ) : null}
            {accessExpiry ? (
              <span
                className="basis-full text-xs tabular-nums text-muted-foreground"
                title={accessExpiry.detail}
              >
                {accessExpiry.label}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function getAccessExpiryDisplay(group: AdminAccountPaymentSummaryAppGroup): {
  label: string;
  detail: string;
} | null {
  const primarySummary = getPrimarySummary(group);
  const expiresAt = primarySummary?.expiresAt
    || group.offerTrialEndsAt
    || (group.activeSubscriptionCount > 0 ? group.nextPaymentAt : null);
  if (!expiresAt) {
    return null;
  }

  const formattedExpiry = formatAdminAccountDateTime(expiresAt);
  const active = group.activeSubscriptionCount > 0;
  return {
    label: `${active ? '만료' : '종료'} ${formattedExpiry}`,
    detail: active ? '현재 이용 기간 만료일' : '마지막 이용 기간 종료일',
  };
}

function getAccessDisplay(group: AdminAccountPaymentSummaryAppGroup): {
  label: string;
  detail: string;
  variant: BadgeProps['variant'];
} {
  const primarySummary = getPrimarySummary(group);
  const detail = primarySummary?.expiresAt
    ? `이용 기준일 ${formatAdminAccountDateTime(primarySummary.expiresAt)}`
    : '이용 종료일 확인 필요';

  if (group.activeSubscriptionCount <= 0) {
    return { label: '이용 없음', detail: '현재 활성 이용권 없음', variant: 'secondary' };
  }
  switch (group.currentAccessSource) {
    case 'PAID_SUBSCRIPTION':
      return { label: '유료 구독 중', detail, variant: 'success' };
    case 'STORE_SUBSCRIPTION':
      return { label: 'Google 구독 중', detail, variant: 'success' };
    case 'STORE_OFFER_TRIAL':
      return { label: 'Offer 무료체험', detail, variant: 'success' };
    case 'COUPON': {
      const isLatestCoupon = group.currentAccessReference
        && group.currentAccessReference === group.latestCouponCode;
      const days = isLatestCoupon ? group.latestCouponTrialDays : null;
      return { label: `쿠폰${days ? ` ${days}일` : ''} 이용 중`, detail, variant: 'warning' };
    }
    case 'REFERRAL_COUPON': {
      const isLatestCoupon = group.currentAccessReference
        && group.currentAccessReference === group.latestCouponCode;
      const days = isLatestCoupon ? group.latestCouponTrialDays : null;
      return { label: `추천인 쿠폰${days ? ` ${days}일` : ''} 이용 중`, detail, variant: 'warning' };
    }
    case 'REFERRAL_REWARD':
      return { label: '추천인 보상 이용 중', detail, variant: 'warning' };
    case 'FIRST_LOGIN_BENEFIT': {
      const isLatestCoupon = group.currentAccessReference
        && group.currentAccessReference === group.latestCouponCode;
      const days = isLatestCoupon ? group.latestCouponTrialDays : null;
      return {
        label: isLatestCoupon
          ? `첫 로그인 쿠폰${days ? ` ${days}일` : ''} 이용 중`
          : '첫 로그인 혜택 이용 중',
        detail,
        variant: 'warning',
      };
    }
    case 'MANUAL_BENEFIT':
      return { label: '운영 혜택 이용 중', detail, variant: 'outline' };
    case 'OTHER_BENEFIT':
      return { label: '기타 혜택 이용 중', detail, variant: 'outline' };
    case 'NONE':
      if (group.activeSubscriptionCount <= 0) {
        return { label: '이용 없음', detail: '현재 활성 이용권 없음', variant: 'secondary' };
      }
      if (group.offerTrialState === 'ACTIVE') {
        return { label: 'Offer 무료체험', detail, variant: 'success' };
      }
      if (group.latestCouponType === 'TRIAL_PLAN' && group.nextPaymentState === 'NONE') {
        return { label: '쿠폰 이용 중', detail, variant: 'warning' };
      }
      if (group.nextPaymentState && group.nextPaymentState !== 'NONE') {
        return { label: '정기구독 중', detail, variant: 'success' };
      }
      return { label: '혜택 이용 중', detail, variant: 'outline' };
    default:
      if (group.offerTrialState === 'ACTIVE') {
        return { label: 'Offer 무료체험', detail, variant: 'success' };
      }
      if (group.latestCouponType === 'TRIAL_PLAN' && group.nextPaymentState === 'NONE') {
        return { label: '쿠폰 이용 중', detail, variant: 'warning' };
      }
      if (group.nextPaymentState && group.nextPaymentState !== 'NONE') {
        return { label: '정기구독 중', detail, variant: 'success' };
      }
      return { label: '혜택 이용 중', detail, variant: 'outline' };
  }
}

function formatCurrentAccessReference(group: AdminAccountPaymentSummaryAppGroup): string {
  switch (group.currentAccessSource) {
    case 'COUPON':
      return `현재 쿠폰 ${group.currentAccessReference}`;
    case 'REFERRAL_COUPON':
      return `추천인 쿠폰 ${group.currentAccessReference}`;
    case 'STORE_OFFER_TRIAL':
      return `Offer ${group.currentAccessReference}`;
    case 'REFERRAL_REWARD':
      return `추천인 ${group.currentAccessReference}`;
    case 'FIRST_LOGIN_BENEFIT':
      return group.currentAccessReference
        ? `첫 로그인 쿠폰 ${group.currentAccessReference}`
        : '첫 로그인 캠페인';
    case 'MANUAL_BENEFIT':
      return `운영 혜택 ${group.currentAccessReference}`;
    default:
      return group.currentAccessReference || '출처 확인';
  }
}

function getHistoryBadges(group: AdminAccountPaymentSummaryAppGroup): Array<{
  label: string;
  detail: string;
  variant: BadgeProps['variant'];
}> {
  const badges: Array<{ label: string; detail: string; variant: BadgeProps['variant'] }> = [];
  const currentCouponSource = ['COUPON', 'REFERRAL_COUPON', 'FIRST_LOGIN_BENEFIT']
    .includes(group.currentAccessSource ?? '');
  const currentCouponCode = group.currentAccessReference?.trim().toUpperCase() ?? '';
  const latestCouponCode = group.latestCouponCode?.trim().toUpperCase() ?? '';
  const latestCouponIsCurrent = currentCouponSource
    && (!currentCouponCode || !latestCouponCode || currentCouponCode === latestCouponCode);

  if ((group.couponUsageCount ?? 0) > 0 && !latestCouponIsCurrent) {
    badges.push({
      label: `과거 쿠폰 ${group.latestCouponCode || '사용'}`,
      detail: formatAdminAccountDateTime(group.latestCouponUsedAt),
      variant: 'outline',
    });
  }
  if ((group.firstLoginBenefitActivityDates?.length ?? 0) > 0
    && group.currentAccessSource !== 'FIRST_LOGIN_BENEFIT') {
    badges.push({ label: '첫 로그인 혜택 이력', detail: '현재 이용 출처와 별도인 과거 혜택', variant: 'outline' });
  }
  if ((group.referralRewardActivityDates?.length ?? 0) > 0
    && group.currentAccessSource !== 'REFERRAL_REWARD'
    && group.currentAccessSource !== 'REFERRAL_COUPON') {
    badges.push({ label: '추천인 보상 이력', detail: '현재 이용 출처와 별도인 과거 추천인 보상', variant: 'outline' });
  }
  if (group.offerTrialState && group.offerTrialState !== 'NONE'
    && group.currentAccessSource !== 'STORE_OFFER_TRIAL') {
    badges.push({
      label: group.offerTrialState === 'ENDED' ? 'Offer 체험 종료' : 'Offer 적용 이력',
      detail: `${formatAdminAccountDateTime(group.offerTrialEndsAt)} · ${formatAdminAccountPaymentPlatform(group.offerPlatform)}`,
      variant: 'outline',
    });
  }
  return badges;
}

function getPrimarySummary(group: AdminAccountPaymentSummaryAppGroup) {
  return group.summaries.find((item) => item.primary) ?? group.summaries[0] ?? null;
}

function getNextPaymentDisplay(group: AdminAccountPaymentSummaryAppGroup): {
  label: string;
  detail: string;
  variant: BadgeProps['variant'];
} {
  if (['COUPON', 'REFERRAL_COUPON', 'REFERRAL_REWARD', 'FIRST_LOGIN_BENEFIT', 'MANUAL_BENEFIT', 'OTHER_BENEFIT']
    .includes(group.currentAccessSource ?? '')) {
    return {
      label: '자동결제 없음',
      detail: '현재 이용 구간은 결제가 아닌 혜택으로 지급됨',
      variant: 'secondary',
    };
  }
  const platform = formatAdminAccountPaymentPlatform(group.nextPaymentPlatform);
  switch (group.nextPaymentState) {
    case 'SCHEDULED':
      return {
        label: '자동결제 예정',
        detail: `${formatAdminAccountDateTime(group.nextPaymentAt)} · ${platform} · 결제수단 등록 확인됨`,
        variant: 'default',
      };
    case 'STORE_AUTORENEWING':
      return {
        label: 'Google 자동갱신',
        detail: `${formatAdminAccountDateTime(group.nextPaymentAt)} · Google Play 자동갱신 켜짐 확인`,
        variant: 'default',
      };
    case 'STORE_NOT_RENEWING':
      return {
        label: 'Google 해지 예정',
        detail: `${formatAdminAccountDateTime(group.nextPaymentAt)} 이용 종료 · Google Play 자동갱신 꺼짐 확인`,
        variant: 'outline',
      };
    case 'STORE_STATUS_UNKNOWN':
      return {
        label: 'Google 갱신 확인 필요',
        detail: '기존 구매 건으로 Google Play 자동갱신 원본 상태가 아직 저장되지 않음',
        variant: 'outline',
      };
    case 'PAYMENT_FAILURE':
      return {
        label: '결제 실패',
        detail: group.nextPaymentAt
          ? `${formatAdminAccountDateTime(group.nextPaymentAt)} · 결제수단 확인 필요`
          : '결제수단 확인 필요',
        variant: 'destructive',
      };
    case 'EXTERNAL_PROVIDER':
      return {
        label: 'Google 갱신 확인 필요',
        detail: '이전 API 응답: Google Play 자동갱신 여부를 다시 확인해야 함',
        variant: 'outline',
      };
    case 'NONE':
    default:
      return {
        label: '자동결제 없음',
        detail: '확인된 정기 자동결제 근거 없음',
        variant: 'secondary',
      };
  }
}
